// Builders for the "Ask AI" affordances. The agent chat normally pins its context from
// the route at chat-creation time; the alerts board is a single route covering every
// contract, so it hands the drawer an EXPLICIT context plus a prefilled first message.
// Pure on purpose — the pin payload is the part worth unit-testing.

import { SeverityCounts } from '../../api/ach/board';
import { ChatContext } from '../../features/agentChat/pageContext';
import parseContractName from './contractName';
import { entityLabel, IssueRef } from './issueRef';
import ruleLabel from './ruleLabels';
import isIORule from './rules';
import { SEVERITY_META, SEVERITY_ORDER } from './severity';

// contractLabel is the advertiser segment when the name parses, else the raw name.
export function contractLabel(contractName: string, contractId: number): string {
  const { advertiser } = parseContractName(contractName);
  return advertiser || contractName || `contract ${contractId}`;
}

// Entity keys are ordered widest -> narrowest: the drawer treats the LAST pinned entity
// as what the agent answers about by default.
export function issueChatContext(item: IssueRef): ChatContext {
  const contractId = item.contract.contract_id;
  return isIORule(item.issue.rule_id)
    ? { pageType: 'insertion_order', entities: { contract_id: contractId, insertion_order_id: item.insertionOrderId } }
    : { pageType: 'campaign', entities: { contract_id: contractId, campaign_id: item.issue.campaign_id } };
}

export function contractChatContext(contractId: number): ChatContext {
  return { pageType: 'contract', entities: { contract_id: contractId } };
}

export function issueChatDraft(item: IssueRef): string {
  const { issue } = item;
  const label = contractLabel(item.contract.contract_name, item.contract.contract_id);
  const lines = [
    `Alert on ${entityLabel(item)} (${label}): ${issue.title}`,
    `Rule: ${ruleLabel(issue.rule_id)} (${issue.rule_id})`,
  ];
  const evidence = issue.evidence.trim();
  if (evidence) {
    lines.push(`Evidence: ${evidence}`);
  }
  lines.push('What is causing this, and how do I fix it?');
  return lines.join('\n');
}

export function contractChatDraft(contractName: string, contractId: number, counts: SeverityCounts): string {
  const breakdown = SEVERITY_ORDER.filter((s) => counts[s] > 0)
    .map((s) => `${counts[s]} ${SEVERITY_META[s].label.toLowerCase()}`)
    .join(', ');
  const label = contractLabel(contractName, contractId);
  return [
    `Triage the open alerts on ${label}${breakdown ? ` (${breakdown})` : ''}.`,
    'Which should I fix first, and what is the fix for each?',
  ].join('\n');
}
